/**
 * @fileoverview V2 Companion Cases Step — Link matter to companion case groups.
 *
 * Self-contained: owns its own state, API calls, and audit record updates.
 * Reuses V1 subcomponents (CompanionOptionSelector, LinkToExistingGroup, etc.)
 * since the UI logic is identical.
 *
 * This step is action-based, not form-based — no useStepHydration needed.
 * Tracks the `companion_cases` audit section as found/not_found.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { AlertTriangle, CheckCircle2, Sparkles } from 'lucide-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import {
  type CompanionChoice,
  CompanionOptionSelector,
} from '@/components/case-ingestion/steps/companion-cases/CompanionOptionSelector';
import { LinkToExistingGroup } from '@/components/case-ingestion/steps/companion-cases/LinkToExistingGroup';
import { PotentialCompanionsPanel } from '@/components/case-ingestion/steps/companion-cases/PotentialCompanionsPanel';
import { PrimaryPickerDialog } from '@/components/case-ingestion/steps/companion-cases/PrimaryPickerDialog';
import { usePotentialCompanions } from '@/components/case-ingestion/hooks/usePotentialCompanions';
import {
  useGetCaseIngestionQuery,
  useUpdateCaseIngestionSectionsMutation,
} from '@/services/api/caseIngestionService';
import {
  useGetMatterCompanionsQuery,
  useGetMatterDetailsQuery,
  usePatchMatterCompanionMembersMutation,
  usePatchMatterCompanionsMutation,
} from '@/services/api/mattersService';

import { FormSection } from '../components/form';
import { useIngestion } from '../state/IngestionProvider';

export const CompanionCasesStep: React.FC = () => {
  const { state, dispatch, registerSave, importData } = useIngestion();
  const matterId = state.matterId!;

  // ── Data fetching ──────────────────────────────────────────────────────
  const { data: matter } = useGetMatterDetailsQuery(matterId ?? skipToken);
  const {
    data: companions,
    isLoading: isLoadingCompanions,
    refetch: refetchCompanions,
  } = useGetMatterCompanionsQuery(matterId);
  const { data: auditRecord } = useGetCaseIngestionQuery(matterId);
  const [updateSections] = useUpdateCaseIngestionSectionsMutation();

  // Mutations
  const [makePrimary, { isLoading: isMakingPrimary }] = usePatchMatterCompanionsMutation();
  const [patchMembers, { isLoading: isPatchingMembers }] = usePatchMatterCompanionMembersMutation();

  // ── Derived data ───────────────────────────────────────────────────────
  const client = matter?.clients?.[0];
  const firmId = matter?.firm?.id ?? null;
  const matterName = matter?.name ?? 'This case';

  const isPrimary = companions?.members.some((m) => m.id === matterId && m.is_primary) ?? false;
  const isGroupMember = (companions?.members.length ?? 0) > 1;
  const hasCompanionStatus = isPrimary || isGroupMember;
  const sectionStatus = auditRecord?.sections?.companion_cases ?? null;

  const isLinking = isMakingPrimary || isPatchingMembers;

  // Potential companions (minor detection + similar matters)
  const { isMinor, similarMatters, isLoading: isLoadingSuggestions } = usePotentialCompanions(
    matterId,
    client?.date_of_birth ?? null,
    client?.last_name ?? null,
    firmId,
  );

  // Import hints
  const importedCompanionNames = importData?.payload?.companion_cases?.related_case_names?.presence === 'present'
    ? importData.payload.companion_cases.related_case_names.value
    : null;

  // ── Local state ────────────────────────────────────────────────────────
  const [selectedChoice, setSelectedChoice] = useState<CompanionChoice | null>(null);
  const [showPrimaryPicker, setShowPrimaryPicker] = useState(false);
  const [pickerTargetMatterId, setPickerTargetMatterId] = useState('');
  const [pickerTargetMatterName, setPickerTargetMatterName] = useState('');

  // Hydrate choice from existing status
  const hasHydratedChoice = useRef(false);
  useEffect(() => {
    if (hasHydratedChoice.current || !companions) return;
    if (isPrimary) {
      hasHydratedChoice.current = true;
      setSelectedChoice('start_group');
    } else if (isGroupMember) {
      hasHydratedChoice.current = true;
      setSelectedChoice('link_existing');
    } else if (sectionStatus === 'not_found') {
      hasHydratedChoice.current = true;
      setSelectedChoice('no_companions');
    } else {
      hasHydratedChoice.current = true;
    }
  }, [companions, isPrimary, isGroupMember, sectionStatus]);

  // ── Save helper ────────────────────────────────────────────────────────
  const updateSection = useCallback(
    async (status: 'found' | 'not_found') => {
      await updateSections({
        matterId,
        data: { sections: { companion_cases: status } },
      }).unwrap();
    },
    [matterId, updateSections],
  );

  // ── Save logic (called on Continue or sidebar navigate) ────────────────
  const saveForm = useCallback(async (): Promise<boolean> => {
    // Already in a group — ensure audit record is up to date
    if (hasCompanionStatus) {
      if (sectionStatus === null) {
        try {
          await updateSection('found');
          toast.success('Companion status confirmed');
        } catch {
          toast.error('Failed to save companion status');
          return false;
        }
      }
      return true;
    }

    // No choice yet — just navigate
    if (!selectedChoice) return true;

    // No companions
    if (selectedChoice === 'no_companions') {
      try {
        await updateSection('not_found');
        toast.success('Marked as no companions');
        return true;
      } catch {
        toast.error('Failed to save');
        return false;
      }
    }

    // Start group — make primary
    if (selectedChoice === 'start_group') {
      try {
        await makePrimary({ matter_id: matterId, make_primary: true }).unwrap();
        await updateSection('found');
        toast.success('Companion group started');
        refetchCompanions();
        return true;
      } catch {
        toast.error('Failed to start companion group');
        return false;
      }
    }

    // Link existing — the actual linking happens via button click, not on save
    return true;
  }, [hasCompanionStatus, sectionStatus, selectedChoice, matterId, makePrimary, updateSection, refetchCompanions]);

  // Register save for sidebar navigation
  useEffect(() => {
    registerSave(saveForm);
    return () => registerSave(null);
  }, [registerSave, saveForm]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const success = await saveForm();
    if (success) {
      dispatch({ type: 'GO_TO_STEP', index: state.stepIndex + 1 });
    }
  };

  // ── Link handlers ──────────────────────────────────────────────────────
  const handleLinkToPrimary = async (primaryMatterId: string) => {
    try {
      await patchMembers({ matter_id: primaryMatterId, add: [matterId] }).unwrap();
      await updateSection('found');
      toast.success('Linked to companion group');
      refetchCompanions();
    } catch {
      toast.error('Failed to link to companion group');
    }
  };

  const handleLinkWithPicker = (targetMatterId: string, targetMatterName: string) => {
    setPickerTargetMatterId(targetMatterId);
    setPickerTargetMatterName(targetMatterName);
    setShowPrimaryPicker(true);
  };

  const handlePrimaryPickerConfirm = async (primaryChoice: 'current' | 'target') => {
    try {
      const primaryId = primaryChoice === 'current' ? matterId : pickerTargetMatterId;
      const memberId = primaryChoice === 'current' ? pickerTargetMatterId : matterId;

      await makePrimary({ matter_id: primaryId, make_primary: true }).unwrap();
      await patchMembers({ matter_id: primaryId, add: [memberId] }).unwrap();
      await updateSection('found');

      toast.success(
        primaryChoice === 'current'
          ? 'Companion group created. This case is the primary.'
          : `Linked to companion group. ${pickerTargetMatterName} is the primary.`,
      );
      setShowPrimaryPicker(false);
      refetchCompanions();
    } catch {
      toast.error('Failed to create companion group');
    }
  };

  const handleQuickLink = (targetMatterId: string) => {
    const target = similarMatters.find((m) => m.id === targetMatterId);
    if (!target) return;
    if (target.is_companion_primary) {
      handleLinkToPrimary(targetMatterId);
    } else {
      handleLinkWithPicker(targetMatterId, target.name);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <form onSubmit={onSubmit} className="space-y-8">
      <FormSection
        title="Companion Cases"
        description="Link this case to related cases in a companion set"
      >
        <div className="space-y-4">
          {/* Import hint */}
          {importedCompanionNames && importedCompanionNames.length > 0 && !hasCompanionStatus && (
            <div className="flex items-start gap-2 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2">
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-blue-500 mt-0.5" />
              <div className="text-[11px] text-blue-700">
                <span className="font-semibold">CMS found related cases:</span>{' '}
                {importedCompanionNames.join(', ')}
              </div>
            </div>
          )}

          {/* Current companion status */}
          {hasCompanionStatus && (
            <div className="flex items-center gap-2 rounded-lg border border-green-200 bg-green-50 px-3 py-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
              <p className="text-sm text-green-800">
                {isPrimary
                  ? 'This case is the primary of a companion group.'
                  : 'Linked to companion group.'}
              </p>
            </div>
          )}

          {/* Group members */}
          {hasCompanionStatus && companions && companions.members.length > 1 && (
            <div className="rounded-lg border border-zinc-200 overflow-hidden">
              <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200">
                <p className="text-xs font-semibold text-zinc-700">
                  Group Members ({companions.members.length})
                </p>
              </div>
              <div className="divide-y divide-zinc-100">
                {companions.members.map((member) => (
                  <div
                    key={member.id}
                    className="flex items-center justify-between px-3 py-2 bg-white text-sm"
                  >
                    {member.id === matterId ? (
                      <span className="font-medium text-zinc-900">
                        {member.display_name} (this case)
                      </span>
                    ) : (
                      <Link
                        to={`/matters/${member.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {member.display_name}
                      </Link>
                    )}
                    {member.is_primary && (
                      <span className="text-xs text-zinc-600 bg-zinc-100 px-2 py-0.5 rounded-full">
                        Primary
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Suggestions (only if not already in a group) */}
          {!hasCompanionStatus && !isLoadingSuggestions && (
            <PotentialCompanionsPanel
              isMinor={isMinor}
              similarMatters={similarMatters}
              onLinkToMatter={handleQuickLink}
              isLinking={isLinking}
            />
          )}

          {/* Option selector (only if not already in a group) */}
          {!hasCompanionStatus && (
            <>
              <div className="rounded-lg border border-zinc-200 p-4 bg-white">
                <CompanionOptionSelector
                  value={selectedChoice}
                  onChange={setSelectedChoice}
                  disabled={isLinking}
                />
              </div>

              {selectedChoice === 'link_existing' && (
                <LinkToExistingGroup
                  firmId={firmId}
                  currentMatterId={matterId}
                  onLinkToPrimary={handleLinkToPrimary}
                  onLinkWithPicker={handleLinkWithPicker}
                  isLinking={isLinking}
                />
              )}
            </>
          )}

          {/* Primary picker dialog */}
          <PrimaryPickerDialog
            open={showPrimaryPicker}
            onOpenChange={setShowPrimaryPicker}
            currentMatterName={matterName}
            targetMatterName={pickerTargetMatterName}
            onConfirm={handlePrimaryPickerConfirm}
            isLoading={isLinking}
          />
        </div>
      </FormSection>

      {/* Submit */}
      <button
        type="submit"
        disabled={isLinking}
        className={
          'inline-flex items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold ' +
          'bg-blue-600 text-white shadow-sm hover:bg-blue-700 ' +
          'disabled:opacity-50 disabled:cursor-not-allowed ' +
          'transition-colors duration-150'
        }
      >
        {isLinking ? 'Saving…' : 'Save & Continue'}
      </button>
    </form>
  );
};
